// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Subject,
} from 'rxjs';

export enum ScreenSize {
  SCREEN_DESKTOP_LARGE = 'screen-desktop-large',
  SCREEN_DESKTOP = 'screen-desktop',
  SCREEN_TABLET = 'screen-tablet',
  SCREEN_PHONE = 'screen-phone',
}

const sizes = [
  ScreenSize.SCREEN_DESKTOP_LARGE,
  ScreenSize.SCREEN_DESKTOP,
  ScreenSize.SCREEN_TABLET,
  ScreenSize.SCREEN_PHONE,
] as const;
export type Orientations = 'landscape' | 'portrait';

/**
 * will manage [screen-xx-xx] and [orientation] attribute on the html tag
 * also manages layout$ observable which can be used for control flow logic in components
 */
export interface ScreenLayout {
  size: ScreenSize;
  orientation: Orientations;
}

@Injectable({
  providedIn: 'root',
})
export class ScreenLayoutService implements OnDestroy {
  private ngZone = inject(NgZone);

  layout$ = new BehaviorSubject<ScreenLayout>({
    size: ScreenSize.SCREEN_DESKTOP_LARGE,
    orientation: 'landscape',
  });
  desktopLarge$ = new BehaviorSubject<boolean>(true);
  desktop$ = new BehaviorSubject<boolean>(false);
  desktopAny$ = new BehaviorSubject<boolean>(true);
  tablet$ = new BehaviorSubject<boolean>(false);
  phone$ = new BehaviorSubject<boolean>(false);
  labdscape$ = new BehaviorSubject<boolean>(true);
  portrait$ = new BehaviorSubject<boolean>(false);

  touchDevice$ = new BehaviorSubject<boolean>(false);

  #forceLayout: ScreenLayout | undefined = undefined;
  forceLayout(layout: ScreenLayout): void {
    this.#forceLayout = layout;
    this.resize();
  }

  private resizeSubject = new Subject<void>();
  private resizeObserver: ResizeObserver;

  constructor() {
    this.resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(() => this.resizeSubject.next()),
    );
    this.resizeObserver.observe(document.body);
    window.addEventListener('resize', this.onResize);

    this.ngZone.run(() => this.resize());
    this.resizeSubject.pipe(debounceTime(100)).subscribe(() => this.resize());
  }

  datePickerMonths$ = combineLatest([this.phone$, this.tablet$]).pipe(
    map(([isPhone, isTablet]) => {
      let months = 3;
      if (isPhone) {
        months = 1;
      } else if (isTablet) {
        months = 2;
      }
      return months;
    }),
  );

  private onResize = () => {
    this.resizeSubject.next();
  };

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  set(layout: ScreenLayout): void {
    this.layout$.next(layout);
    this.desktopLarge$.next(layout.size === 'screen-desktop-large');
    this.desktop$.next(layout.size === 'screen-desktop');
    this.desktopAny$.next(
      layout.size === 'screen-desktop' ||
        layout.size === 'screen-desktop-large',
    );
    this.tablet$.next(layout.size === 'screen-tablet');
    this.phone$.next(layout.size === 'screen-phone');
    this.labdscape$.next(layout.orientation === 'landscape');
    this.portrait$.next(layout.orientation === 'portrait');

    this.touchDevice$.next(
      'ontouchstart' in window || navigator.maxTouchPoints > 0,
    );
  }

  resize = (): void => {
    if (this.#forceLayout != undefined) {
      this.set(this.#forceLayout as ScreenLayout);
      return;
    }
    // might also be useful here!
    const viewport = document.head.querySelector('meta[name=viewport]');
    const w = window.innerWidth;
    let size = ScreenSize.SCREEN_DESKTOP_LARGE;
    if (w < 769) {
      size = ScreenSize.SCREEN_PHONE;
      viewport?.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
      );
    } else if (w < 1025) {
      size = ScreenSize.SCREEN_TABLET;
      viewport?.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
      );
    } else if (w < 1440) {
      size = ScreenSize.SCREEN_DESKTOP;
      viewport?.setAttribute('content', 'width=device-width, initial-scale=1');
    } else {
      viewport?.setAttribute('content', 'width=device-width, initial-scale=1');
    }

    const html = document.querySelector('html')!;

    const supportsOrientation = !!screen.orientation.type;
    // detect ipads/tablets
    const isTouchdevice = window.matchMedia('(pointer: coarse)').matches;
    if (isTouchdevice && supportsOrientation) {
      if (window.matchMedia('(orientation: landscape)').matches && w < 1367) {
        size = ScreenSize.SCREEN_TABLET;
      }
    }

    sizes.forEach((s) => s !== size && html.removeAttribute(s));
    html.setAttribute(size, '');

    let orientation: Orientations = 'landscape';
    if (supportsOrientation) {
      html.removeAttribute('portrait');
      html.removeAttribute('landscape');
      if (screen.orientation.type.match(/landscape/i)) {
        html.setAttribute('landscape', '');
      } else if (screen.orientation.type.match(/portrait/i)) {
        html.setAttribute('portrait', '');
        orientation = 'portrait';
      }
    }
    this.set({ orientation, size });
  };
}
